/**
 * Contact-photo API wrappers (dink-api `/v2/admin/members/{contactId}/photo`,
 * DNKADM-0048). Consumes the DNKAPI-0104 contract:
 *
 *  - `PUT  /v2/admin/members/{contactId}/photo` — **`multipart/form-data`** with the
 *    cropped image as the file part → `200 { contactId, photoUrl, contentType,
 *    byteSize, updatedAt }`. dink-api resizes to 256×256 and owns the stored image.
 *  - `DELETE /v2/admin/members/{contactId}/photo` → `204` (idempotent — 204 even when
 *    there was no photo).
 *  - `GET /members/{contactId}/photo` → the image bytes (404 = no photo). The read
 *    REQUIRES the bearer (confirmed live 2026-08-14: a plain `<img src>` gets a 401
 *    — dink-api's serve route reads the JWT for tenant scoping), so the bytes are
 *    fetched through the authed client and rendered via an object URL
 *    ({@link fetchMemberPhotoBlob} + `useMemberPhotoObjectUrl`, DNKADM-0304 U4 —
 *    the move this docblock predicted).
 *
 * Bearer-only `/v2/admin/**` surface (NO CSRF, NO cookies — handled by `request`); the
 * bounded 401→refresh→retry applies. **First multipart upload in the repo** — the
 * `request()` wrapper grew a `form` (FormData) body path that sends no explicit
 * `Content-Type`, letting the browser set the `multipart/form-data` boundary
 * (DNKADM-0048).
 *
 * Errors map per the DNKAPI-0104 table: `400`/`413` too large, `400`/`415` wrong
 * type, `403` not your tenant / not OWNER-ADMIN. {@link toMemberPhotoErrorMessage}
 * turns a thrown `ApiError` into the staff-facing copy.
 */
import { request } from './client';
import { ApiError } from './errors';
import { serverMessage } from './serverMessage';
import { env } from '@/config/env';

/** Parsed `PUT /v2/admin/members/{contactId}/photo` result (present-fields-only). */
export interface MemberPhotoResult {
  contactId: string | null;
  /** The (possibly per-contact-stable) photo path/URL — render via {@link photoSrc}. */
  photoUrl: string | null;
  /** The stored image's content type (e.g. `image/webp` | `image/jpeg`). */
  contentType: string | null;
  /** Stored byte size of the 256² image. */
  byteSize: number | null;
  /** Last-updated instant — the cache-bust key (see {@link photoSrc}). */
  updatedAt: string | null;
}

// --- Defensive parsing helpers (mirror members.ts) -----------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** The multipart file-part field name (DNKAPI-0104). */
const PHOTO_FIELD = 'file';

function photoPath(contactId: string): string {
  return `/v2/admin/members/${encodeURIComponent(contactId)}/photo`;
}

/**
 * `PUT /v2/admin/members/{contactId}/photo` — set or replace a contact's photo with a
 * **`multipart/form-data`** body (the cropped square `blob` as the `file` part).
 * Send a reasonable JPEG/PNG/WebP blob; dink-api does the authoritative 256² resize.
 * Bearer-only (no CSRF, no cookies — `request` handles it); the bounded
 * 401→refresh→retry applies. Parses `{ contactId, photoUrl, contentType, byteSize,
 * updatedAt }` defensively (present-fields-only; absent/wrong-typed → null). Throws a
 * typed `ApiError` on a non-2xx response — `400`/`413` (too large), `400`/`415`
 * (wrong type), `403` (not your tenant / not OWNER-ADMIN); map it with
 * {@link toMemberPhotoErrorMessage}.
 */
export async function setMemberPhoto(
  contactId: string,
  blob: Blob,
  filename?: string,
): Promise<MemberPhotoResult> {
  const form = new FormData();
  // Wrap the blob in a File so the multipart part carries a stable filename and
  // content type. Passing the filename as `append`'s third arg is unreliable for a
  // plain Blob in some environments (it can default to "blob"); a File pins it. The
  // file part field name is `file` (DNKAPI-0104).
  const name = filename ?? 'photo';
  const file = blob instanceof File && blob.name ? blob : new File([blob], name, { type: blob.type });
  form.append(PHOTO_FIELD, file);

  const body = await request<unknown>('PUT', photoPath(contactId), { form });

  const o = isObject(body) ? body : {};
  return {
    contactId: asText(o['contactId']),
    photoUrl: asText(o['photoUrl']),
    contentType: asText(o['contentType']),
    byteSize: asNumber(o['byteSize']),
    updatedAt: asText(o['updatedAt']),
  };
}

/**
 * `DELETE /v2/admin/members/{contactId}/photo` — remove a contact's photo. Idempotent:
 * dink-api returns `204` even when there was no photo, and `request` returns
 * `undefined` for a 204 (no body), so a successful delete simply resolves. Bearer-only
 * (no CSRF, no cookies). Throws a typed `ApiError` on a non-2xx response — `403` (not
 * your tenant / not OWNER-ADMIN) is the notable case.
 */
export async function deleteMemberPhoto(contactId: string): Promise<void> {
  await request<void>('DELETE', photoPath(contactId));
}

/**
 * Normalize a `photoUrl` into the request PATH the authed client takes
 * (DNKADM-0304 U4). The wire value is the bare serve path
 * (`/members/{contactId}/photo`, DNKAPI-0104 verbatim); an absolute URL on the
 * app's own api origin is reduced to its path, and a FOREIGN absolute URL
 * returns null — the bearer must never be sent cross-origin, so such a value
 * degrades to the initials avatar rather than an unauthenticated fetch.
 */
export function photoRequestPath(photoUrl: string | null | undefined): string | null {
  const url = asText(photoUrl ?? null);
  if (url === null) return null;
  if (url.startsWith('http')) {
    const base = env.journixApiBaseUrl;
    return url.startsWith(`${base}/`) ? url.slice(base.length) : null;
  }
  return url.startsWith('/') ? url : `/${url}`;
}

/**
 * Fetch a contact photo's BYTES through the authed client (DNKADM-0304 U4) —
 * bearer attached, bounded 401→refresh→retry, no cookies (the serve path is on
 * the bearer surface, `client.ts`). `cacheBust` keeps the pre-existing
 * `?v={reloadToken}` behaviour so a just-replaced photo is re-read, not served
 * stale. **Fail-soft by contract:** any failure — no photo (404), a foreign
 * `photoUrl`, a network error — resolves `null` and the caller keeps the
 * initials avatar; this read never surfaces an error state.
 */
export async function fetchMemberPhotoBlob(
  photoUrl: string | null | undefined,
  cacheBust?: string | null,
): Promise<Blob | null> {
  const path = photoRequestPath(photoUrl);
  if (path === null) return null;
  try {
    const blob = await request<Blob>('GET', path, {
      responseType: 'blob',
      ...(asText(cacheBust ?? null) !== null ? { query: { v: cacheBust as string } } : {}),
    });
    return blob instanceof Blob && blob.size > 0 ? blob : null;
  } catch {
    return null;
  }
}

/**
 * Staff-facing copy for a failed photo set/remove (DNKADM-0048), mapping the typed
 * `ApiError` to the DNKAPI-0104 error table:
 *  - `413` / a too-large `400` → "Image must be under 5 MB.";
 *  - `415` / a wrong-type `400` → "Use a JPG, PNG, or WebP image.";
 *  - `403` → "You don’t have access to this contact.";
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect);
 *  - anything else → the server's message, then a generic line.
 *
 * `413` and `415` are not in `statusToCode`'s small union, so a `request()` throw maps
 * them to `UNKNOWN` — we branch on `error.status` to recover the precise copy. A
 * generic `400` (VALIDATION) defaults to the wrong-type line (the likeliest cause when
 * the size is OK), preferring the server's own message when present.
 */
export function toMemberPhotoErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    if (error.status === 413) return 'Image must be under 5 MB.';
    if (error.status === 415) return 'Use a JPG, PNG, or WebP image.';
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'Use a JPG, PNG, or WebP image.';
      case 'FORBIDDEN':
        return 'You don’t have access to this contact.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The photo could not be updated. Please try again.';
}
